import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { api } from "../context/AuthContext";
import { money } from "../lib/format";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Bank, Plus, Minus, TrendUp } from "@phosphor-icons/react";
import { toast } from "sonner";

export const CapitalPanel = () => {
  const { t } = useI18n();
  const [data, setData] = useState({ summary: {}, movements: [] });
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("DOP");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true);
    setError(false);
    api.get("/admin/capital").then((r) => setData(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async (type = "deposit") => {
    const v = Number(amount);
    if (!v || v <= 0) { toast.error(t("invalid_amount")); return; }
    setBusy(true);
    try {
      await api.post("/admin/capital", { amount: v, currency, note, type });
      toast.success(type === "deposit" ? t("capital_added_ok") : t("capital_deducted_ok")); setAmount(""); setNote(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const currencies = ["DOP", "USD"];

  return (
    <div className="space-y-6" data-testid="capital-panel">
      <div className="flex items-center gap-2">
        <Bank size={22} weight="duotone" className="text-emerald-700" />
        <h3 className="font-display font-bold text-emerald-900 text-xl">{t("capital_title")}</h3>
      </div>

      {loading ? <p className="text-slate-500">{t("loading")}</p> : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center" data-testid="capital-error">
          <p className="text-red-700 font-semibold">{t("capital_load_error")}</p>
          <Button data-testid="capital-retry" onClick={load} className="mt-3 bg-emerald-800 hover:bg-emerald-900">{t("retry")}</Button>
        </div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-5">
            {(currencies.length ? currencies : ["DOP"]).map((c) => {
              const s = data.summary[c] || {};
              return (
                <div key={c} className="bg-white rounded-xl border border-gray-100 shadow-sm p-6" data-testid={`capital-card-${c}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-display font-bold text-emerald-900">{c}</span>
                    <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-sm"><TrendUp size={16} weight="bold" /> {t("growth")}: {s.growth_pct || 0}%</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><p className="text-slate-400 text-xs">{t("capital_contributed")}</p><p className="font-mono-num font-bold text-emerald-900" data-testid={`capital-total-${c}`}>{money(s.capital, c)}</p></div>
                    <div><p className="text-slate-400 text-xs">{t("growth_amount")}</p><p className="font-mono-num font-bold text-emerald-700">{money(s.growth, c)}</p></div>
                    <div><p className="text-slate-400 text-xs">{t("loans_out")}</p><p className="font-mono-num text-emerald-900">{money(s.loans_principal, c)}</p></div>
                    <div><p className="text-slate-400 text-xs">{t("loans_collected")}</p><p className="font-mono-num text-emerald-900">{money(s.loans_collected, c)}</p></div>
                    <div><p className="text-slate-400 text-xs">{t("savings_balance_total")}</p><p className="font-mono-num text-emerald-900">{money(s.savings_balance, c)}</p></div>
                    <div><p className="text-slate-400 text-xs">{t("term_deposits")}</p><p className="font-mono-num text-emerald-900">{money(s.term_deposit_principal, c)}</p></div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
            <h4 className="font-display font-bold text-emerald-900 mb-4">{t("capital_movement")}</h4>
            <div className="grid sm:grid-cols-5 gap-3 items-end">
              <div><Label>{t("mv_amount")}</Label><Input data-testid="capital-amount" type="number" step="0.01" min="0" className="mt-1 h-11 font-mono-num" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
              <div>
                <Label>{t("currency")}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger data-testid="capital-currency" className="mt-1 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="DOP">RD$ (DOP)</SelectItem><SelectItem value="USD">US$ (USD)</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-1"><Label>{t("note")}</Label><Input data-testid="capital-note" className="mt-1 h-11" value={note} onChange={(e) => setNote(e.target.value)} /></div>
              <Button data-testid="capital-submit" onClick={() => submit("deposit")} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 h-11 gap-1.5"><Plus size={16} weight="bold" /> {t("add_capital")}</Button>
              <Button data-testid="capital-withdraw" onClick={() => submit("withdrawal")} disabled={busy} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50 h-11 gap-1.5"><Minus size={16} weight="bold" /> {t("deduct_capital")}</Button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>{t("date")}</TableHead><TableHead>{t("type")}</TableHead><TableHead className="text-right">{t("mv_amount")}</TableHead><TableHead>{t("note")}</TableHead><TableHead>{t("by")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.movements.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-8">—</TableCell></TableRow>
                ) : data.movements.map((m) => {
                  const isWithdrawal = (m.type === "withdrawal") || (m.amount < 0);
                  return (
                  <TableRow key={m.id} data-testid={`capital-row-${m.id}`}>
                    <TableCell className="text-sm">{(m.created_at || "").slice(0, 10)}</TableCell>
                    <TableCell><span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${isWithdrawal ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{isWithdrawal ? t("withdrawal") : t("deposit")}</span></TableCell>
                    <TableCell className={`text-right font-mono-num font-bold ${isWithdrawal ? "text-amber-700" : "text-emerald-800"}`}>{money(m.amount, m.currency)}</TableCell>
                    <TableCell className="text-sm text-slate-600">{m.note || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-500">{m.by || "—"}</TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};
